import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

type Tool = 'highlight' | 'blackout';
type Rect = { x: number; y: number; w: number; h: number; type: Tool };

export type AnnotatorHandle = {
  getCompositeBlob: () => Promise<Blob | null>;
  hasImage: () => boolean;
};

type Props = { imageDataUrl: string };

const normalize = (r: Rect) => ({
  x: Math.min(r.x, r.x + r.w),
  y: Math.min(r.y, r.y + r.h),
  w: Math.abs(r.w),
  h: Math.abs(r.h),
  type: r.type,
});

const paintRect = (ctx: CanvasRenderingContext2D, r: Rect, lineWidth = 3) => {
  const n = normalize(r);
  if (n.type === 'highlight') {
    ctx.fillStyle = 'rgba(255,0,0,0.22)';
    ctx.fillRect(n.x, n.y, n.w, n.h);
    ctx.strokeStyle = 'rgba(220,0,0,0.95)';
    ctx.lineWidth = lineWidth;
    ctx.strokeRect(n.x, n.y, n.w, n.h);
  } else {
    ctx.fillStyle = '#111318';
    ctx.fillRect(n.x, n.y, n.w, n.h);
  }
};

const ScreenshotAnnotator = forwardRef<AnnotatorHandle, Props>(({ imageDataUrl }, ref) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef<Rect | null>(null);

  const [tool, setTool] = useState<Tool>('highlight');
  const [rects, setRects] = useState<Rect[]>([]);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [display, setDisplay] = useState({ w: 0, h: 0 });

  // Görsel yüklendiğinde gösterim boyutunu hesapla
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const containerW = wrapRef.current?.clientWidth || 760;
      const w = Math.min(img.naturalWidth, containerW);
      const scale = img.naturalWidth ? w / img.naturalWidth : 1;
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
      setDisplay({ w: Math.round(w), h: Math.round(img.naturalHeight * scale) });
      setRects([]);
    };
    img.src = imageDataUrl;
  }, [imageDataUrl]);

  // Overlay'i yeniden çiz
  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    rects.forEach((r) => paintRect(ctx, r));
    if (drawingRef.current) paintRect(ctx, drawingRef.current);
  };

  useEffect(redraw, [rects, display]);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const isTouch = 'touches' in e;
    const clientX = isTouch ? (e as React.TouchEvent).touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = isTouch ? (e as React.TouchEvent).touches[0].clientY : (e as React.MouseEvent).clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const onDown = (e: React.MouseEvent | React.TouchEvent) => {
    const p = getPos(e);
    drawingRef.current = { x: p.x, y: p.y, w: 0, h: 0, type: tool };
  };
  const onMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current) return;
    const p = getPos(e);
    drawingRef.current = { ...drawingRef.current, w: p.x - drawingRef.current.x, h: p.y - drawingRef.current.y };
    redraw();
  };
  const onUp = () => {
    const d = drawingRef.current;
    if (d && Math.abs(d.w) > 4 && Math.abs(d.h) > 4) {
      setRects((prev) => [...prev, d]);
    }
    drawingRef.current = null;
    redraw();
  };

  useImperativeHandle(
    ref,
    () => ({
      hasImage: () => !!imageDataUrl,
      getCompositeBlob: () =>
        new Promise((resolve) => {
          if (!imageDataUrl) return resolve(null);
          const base = new Image();
          base.onload = () => {
            const out = document.createElement('canvas');
            out.width = natural.w || base.naturalWidth;
            out.height = natural.h || base.naturalHeight;
            const ctx = out.getContext('2d');
            if (!ctx) return resolve(null);
            ctx.drawImage(base, 0, 0, out.width, out.height);
            const scale = display.w ? out.width / display.w : 1;
            rects.forEach((r) => {
              paintRect(
                ctx,
                { x: r.x * scale, y: r.y * scale, w: r.w * scale, h: r.h * scale, type: r.type },
                Math.max(2, 3 * scale),
              );
            });
            out.toBlob((b) => resolve(b), 'image/jpeg', 0.85);
          };
          base.onerror = () => resolve(null);
          base.src = imageDataUrl;
        }),
    }),
    [imageDataUrl, natural, display, rects],
  );

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <div className="btn-group btn-group-sm" role="group">
          <button
            type="button"
            className={`btn ${tool === 'highlight' ? 'btn-danger' : 'btn-light-danger'}`}
            onClick={() => setTool('highlight')}
          >
            <i className="bi bi-highlighter me-1" /> Vurgula
          </button>
          <button
            type="button"
            className={`btn ${tool === 'blackout' ? 'btn-dark' : 'btn-light-dark'}`}
            onClick={() => setTool('blackout')}
          >
            <i className="bi bi-square-fill me-1" /> Karart
          </button>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-light"
          onClick={() => setRects((r) => r.slice(0, -1))}
          disabled={!rects.length}
        >
          <i className="bi bi-arrow-counterclockwise me-1" /> Geri Al
        </button>
        <button type="button" className="btn btn-sm btn-light" onClick={() => setRects([])} disabled={!rects.length}>
          <i className="bi bi-trash me-1" /> Temizle
        </button>
      </div>

      <div
        ref={wrapRef}
        style={{
          position: 'relative',
          width: '100%',
          maxHeight: 420,
          overflow: 'auto',
          border: '1px solid #e4e6ef',
          borderRadius: 8,
          background: '#f9f9fb',
        }}
      >
        <img src={imageDataUrl} alt="Ekran görüntüsü" style={{ display: 'block', width: display.w, height: display.h }} />
        <canvas
          ref={canvasRef}
          width={display.w}
          height={display.h}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: display.w,
            height: display.h,
            cursor: 'crosshair',
            touchAction: 'none',
          }}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={onUp}
          onTouchStart={onDown}
          onTouchMove={onMove}
          onTouchEnd={onUp}
        />
      </div>
      <div className="text-muted fs-8 mt-1">
        <i className="bi bi-info-circle me-1" />
        Sorunlu alanı işaretlemek için sürükleyin. Hassas bilgileri <strong>Karart</strong> ile gizleyebilirsiniz.
      </div>
    </div>
  );
});

ScreenshotAnnotator.displayName = 'ScreenshotAnnotator';

export default ScreenshotAnnotator;
